import { useState } from 'react';

import Popup from '@/components/Popup/PopUp/PopUp';
import { Loading } from '@/components/shared/Loading/Loading';
import { Text } from '@/components/shared/Text/Text';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { ExploreBlock, ExploreBlockType } from '@addmile/backend-client';
import { useQuery } from '@tanstack/react-query';

import ContentStatus from '../../../../Content/components/ContentStatus/ContentStatus';
import { BLOCK_TYPES } from '../../../../Content/const';
import {
  ContentManagerService,
  contentKeys,
} from '../../../../Content/services/ContentManagerService';

const PAGE_SIZE = 20;

interface BlockPickerModalProps {
  type?: ExploreBlockType;
  onPick: (block: ExploreBlock) => void;
  onClose: () => void;
}

const BlockPickerModal = ({ type, onPick, onClose }: BlockPickerModalProps) => {
  const [title, setTitle] = useState('');
  const debouncedTitle = useDebounce(title, 300);
  const params = {
    type: type ? [type] : [],
    title: debouncedTitle || undefined,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: contentKeys.blocksList(params),
    queryFn: () => ContentManagerService.getBlocks(params),
    enabled: !!type,
  });

  const typeLabel = BLOCK_TYPES.find(({ value }) => value === type)?.label ?? 'block';

  const renderBlocks = () => {
    if (isLoading) return <Loading />;
    if (error) {
      return <Text.Regular className="text-danger-60">Failed to load blocks.</Text.Regular>;
    }
    if (!data?.blocks.length) {
      return <Text.Regular className="text-neutral-50">No {typeLabel} blocks match.</Text.Regular>;
    }

    return (
      <ul className="grid gap-2">
        {data.blocks.map((block) => (
          <li key={block.slug}>
            <button
              type="button"
              onClick={() => onPick(block)}
              className="w-full flex items-center gap-3 p-2 rounded-xl border border-neutral-20 text-left hover:bg-primary-10"
            >
              {block.imageUrl ? (
                <img src={block.imageUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-neutral-10" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-neutral-90 truncate">{block.title}</p>
                <p className="text-xs text-neutral-50 truncate">{block.slug}</p>
              </div>
              <ContentStatus status={block.status} />
            </button>
          </li>
        ))}
        {data.hasMore && (
          <Text.Regular className="text-neutral-50 text-sm">
            Showing the first {PAGE_SIZE} — search by title to narrow it down.
          </Text.Regular>
        )}
      </ul>
    );
  };

  return (
    <Popup isOpen={!!type} handleClose={onClose} maxWidth={560}>
      <Text.Big weight="bold" className="mb-1">
        Add {typeLabel}
      </Text.Big>
      <Text.Regular className="text-neutral-60 mb-4">
        Pick an existing {typeLabel} block from Explore.
      </Text.Regular>
      <input
        autoFocus
        className="w-full px-3 py-2 mb-4 border border-neutral-20 rounded-lg text-sm"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Search by title..."
      />
      <div className="max-h-[50vh] overflow-auto">{renderBlocks()}</div>
    </Popup>
  );
};

export default BlockPickerModal;
